import { UserInteraction, UserInteractions } from "@hex/common";
import { useCallback, useMemo } from "react";

import { useSetInteractionsMutation } from "../../mutations/user.generated.js";
import { useCurrentUser } from "../me/useCurrentUser.js";

/**
 * Returns value of user interaction key and a setter that optimistically updates user interactions
 */
export function useUserInteractions<K extends keyof UserInteractions>(
  key: K,
): [
  UserInteractions[K],
  (value: UserInteractions[typeof key]) => Promise<void>,
] {
  const [setUserInteractions] = useSetInteractionsMutation();
  const currentUser = useCurrentUser();

  const DefaultUserInteractions: UserInteractions = useMemo(() => {
    return {
      [UserInteraction.TEMPLATE_DATA_SOURCES_TOOLTIP_SHOWN]: false,
      [UserInteraction.TEMPLATE_SIDEBAR_TOOLTIP_SHOWN]: false,
      [UserInteraction.PROJECT_ONBOARDING_DISMISSED]: false,
      [UserInteraction.PROJECT_ONBOARDING_COLLAPSED]: false,
      [UserInteraction.EXPLORE_FROM_HERE_CALLOUT_DISMISSED]: false,
      [UserInteraction.PRESENTATION_MODE_CALLOUT_DISMISSED]: false,
      [UserInteraction.NEW_PROJECT_TOUR_STARTED]: false,
      [UserInteraction.APP_VIEW_TOUR_STARTED]: false,
      [UserInteraction.DEBUG_MODE_CALLOUT_DISMISSED]: false,
      [UserInteraction.NOTIFICATION_INBOX_OPENED]: false,
      [UserInteraction.HOME_PAGE_ONBOARDING_DISMISSED]: false,
      [UserInteraction.LEARN_SIDEBAR_DEFAULT_OPEN]: false,
      [UserInteraction.CALCS_AWARENESS_INDICATOR_DISMISSED]: false,
      [UserInteraction.ENDORSED_DATA_MAGIC_AWARENESS_INDICATOR_DISMISSED]: false,
      [UserInteraction.CONDITIONAL_NOTIFICATION_ICON_CLICKED]: false,
      [UserInteraction.ASK_MAGIC_INDICATOR_DISMISSED]: false,
      [UserInteraction.SAVED_VIEW_CTA_CLICKED]: false,
      [UserInteraction.SEMANTIC_MODEL_AWARENESS_INDICATOR_DISMISSED]: false,
      [UserInteraction.MAGIC_2025_TOS_DIALOG_DISMISSED]: false,

      [UserInteraction.EXPLORE_ENDORSED_DATA_INFO_DISMISSED]: false,
      [UserInteraction.THREADS_TALK_TO_DATA_INFO_DISMISSED]: false,
      [UserInteraction.THREADS_INTRO_DIALOG_DISMISSED]: false,
      [UserInteraction.TRIAL_ENDING_DIALOG_DISMISSED]: false,
      [UserInteraction.CREATOR_ONBOARDING_COMPLETED]: false,
      [UserInteraction.THREADS_SLACK_POPOVER_DISMISSED]: false,
      [UserInteraction.THREADS_SLACK_ADMIN_POPOVER_DISMISSED]: false,
      [UserInteraction.CHAT_WITH_APP_AWARENESS_INDICATOR_DISMISSED]: false,
      [UserInteraction.SQL_CELL_OUTPUT_AWARENESS_INDICATOR_DISMISSED]: false,
      [UserInteraction.CONTEXT_STUDIO_SETUP_DISMISSED]: false,
    };
  }, []);

  const interactions = useMemo(
    () => ({
      ...DefaultUserInteractions,
      ...currentUser?.interactions,
    }),
    [currentUser?.interactions, DefaultUserInteractions],
  );

  return [
    interactions[key],
    useCallback(
      async (value: UserInteractions[typeof key]) => {
        await setUserInteractions({
          variables: {
            interactions: { [key]: value },
          },
          optimisticResponse: currentUser?.id
            ? {
                __typename: "Mutation",
                setInteractions: {
                  id: currentUser.id,
                  __typename: "User",
                  interactions: { ...interactions, [key]: value },
                },
              }
            : undefined,
        });
      },
      [currentUser?.id, key, interactions, setUserInteractions],
    ),
  ];
}
